import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { Mail, Smartphone, Loader2, KeyRound, Key } from 'lucide-react';
import { toast } from 'sonner';
import { LogoShield } from '@/components/LogoShield';
import { AltchaWidget } from '@/components/AltchaWidget';
import { invokeMfa } from '@/lib/mfa-invoke';

interface MfaVerificationProps {
  userId: string;
  email: string;
  mfaMethod: 'totp' | 'email';
  availableMethods?: string[];
  onSuccess: (session?: Session | null) => void;
  onCancel: () => void;
  onSwitchMethod?: (method: 'totp' | 'email') => void;
}

type RecoveryMode = 'none' | 'email' | 'backup';

export function MfaVerification({ userId, email, mfaMethod, availableMethods = [], onSuccess, onCancel, onSwitchMethod }: MfaVerificationProps) {
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [factorsLoading, setFactorsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [recoveryMode, setRecoveryMode] = useState<RecoveryMode>('none');
  const [totpFactorId, setTotpFactorId] = useState<string | null>(null);
  const [altchaToken, setAltchaToken] = useState<string | null>(null);
  const wasEmailModeRef = useRef(false);
  const isEmailMode = mfaMethod === 'email' || recoveryMode === 'email';

  const getErrorMessage = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err && 'message' in err) {
      const msg = (err as { message?: unknown }).message;
      return typeof msg === 'string' ? msg : '';
    }
    return '';
  };

  const sendEmailCode = useCallback(async () => {
    if (cooldown > 0) return;
    
    setIsSending(true);
    try {
      const { error } = await invokeMfa({
        body: { action: 'send', email, user_id: userId },
        altchaToken,
      });

      if (error) {
        throw error;
      }

      toast.success('Verification code sent to your email');
      setCooldown(60);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('Error sending MFA code:', errorMessage || err);
      toast.error('Failed to send verification code');
    } finally {
      setIsSending(false);
    }
  }, [altchaToken, cooldown, email, userId]);

  useEffect(() => {
    if (isEmailMode && !wasEmailModeRef.current) {
      void sendEmailCode();
    }
    wasEmailModeRef.current = isEmailMode;
  }, [isEmailMode, sendEmailCode]);

  useEffect(() => {
    const loadFactors = async (retryCount = 0) => {
      setFactorsLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          if (retryCount < 3) {
            setTimeout(() => loadFactors(retryCount + 1), 500);
            return;
          }
          console.error('No session available for MFA factor listing');
          setFactorsLoading(false);
          return;
        }

        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) {
          console.error('Error listing factors:', error);
          setFactorsLoading(false);
          return;
        }

        const totp = data.totp?.find((factor) => factor.status === 'verified') || data.totp?.[0];
        setTotpFactorId(totp?.id || null);
      } catch (err) {
        console.error('Exception loading factors:', err);
      } finally {
        setFactorsLoading(false);
      }
    };

    if (mfaMethod === 'totp' && !totpFactorId) {
      loadFactors();
    }
  }, [mfaMethod, totpFactorId]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const checkLoginLocation = async () => {
    try {
      await supabase.functions.invoke('check-login-location', {
        body: JSON.stringify({ user_id: userId, email }),
      });
    } catch (err) {
      console.error('Error checking login location:', err);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      if (mfaMethod === 'email' || recoveryMode === 'email') {
        const { data, error } = await invokeMfa({
          body: { action: 'verify', user_id: userId, code },
          altchaToken,
        });

        if (error || !data?.verified) {
          throw new Error((data as Record<string, unknown>)?.error as string || 'Invalid verification code');
        }

        await checkLoginLocation();
        toast.success('Verification successful');
        onSuccess();
      } else {
        if (!totpFactorId) {
          toast.error('No authenticator factor found. Please try another method.');
          setIsLoading(false);
          return;
        }
        const { data, error } = await supabase.auth.mfa.challengeAndVerify({
          factorId: totpFactorId,
          code,
        });

        if (error) {
          throw error;
        }

        await checkLoginLocation();
        toast.success('Verification successful');
        onSuccess();
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('MFA verification error:', errorMessage || err);
      toast.error(errorMessage || 'Invalid verification code');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyBackupCode = async () => {
    const cleanCode = backupCode.toUpperCase().replace(/\s/g, '');
    if (cleanCode.length !== 8) {
      toast.error('Please enter a valid 8-character backup code');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await invokeMfa({
        body: { action: 'verify-backup-code', user_id: userId, code: cleanCode },
        altchaToken,
      });

      if (error || !data?.verified) {
        throw new Error((data as Record<string, unknown>)?.error as string || 'Invalid backup code');
      }

      await checkLoginLocation();
      toast.success('Backup code verified successfully');
      onSuccess();
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('Backup code verification error:', errorMessage || err);
      toast.error(errorMessage || 'Invalid backup code');
      setBackupCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseEmailRecovery = () => {
    setRecoveryMode('email');
    setCode('');
    setBackupCode('');
  };

  const handleUseBackupCode = () => {
    setRecoveryMode('backup');
    setCode('');
    setBackupCode('');
  };

  const handleBackToTotp = () => {
    setRecoveryMode('none');
    setCode('');
    setBackupCode('');
    setCooldown(0);
  };

  const isBackupMode = recoveryMode === 'backup';
  const isTotpUnavailable = mfaMethod === 'totp' && !totpFactorId && !factorsLoading;
  const handleCodeChange = (val: string) => setCode(val.replace(/\D/g, '').slice(0, 6));

  // Backup code entry screen
  if (isBackupMode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <Card className="w-full max-w-md cyber-card relative z-10">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Use Backup Code
            </CardTitle>
            <CardDescription>
              Enter one of your 8-character backup codes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Input
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="XXXXXXXX"
              maxLength={8}
              className="text-center font-mono text-lg tracking-widest bg-muted/50 border-border"
              disabled={isLoading}
            />

            <AltchaWidget onToken={setAltchaToken} className="flex justify-center" />

            <Button 
              onClick={verifyBackupCode} 
              className="w-full" 
              disabled={isLoading || backupCode.replace(/\s/g, '').length !== 8 || !altchaToken}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Backup Code'
              )}
            </Button>

            <Button
              variant="ghost"
              onClick={handleBackToTotp}
              className="w-full text-muted-foreground"
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Back to authenticator
            </Button>

            <Button variant="outline" onClick={onCancel} className="w-full">
              Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <Card className="w-full max-w-md cyber-card relative z-10">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoShield className="h-8 w-8" />
            <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
          </div>
          <CardTitle className="text-xl flex items-center justify-center gap-2">
            {isEmailMode ? (
              <>
                <Mail className="h-5 w-5 text-primary" />
                {recoveryMode === 'email' ? 'Recovery Verification' : 'Two-Factor Authentication'}
              </>
            ) : (
              <>
                <Smartphone className="h-5 w-5 text-primary" />
                Two-Factor Authentication
              </>
            )}
          </CardTitle>
          <CardDescription>
            {isEmailMode 
              ? `Enter the 6-digit code sent to ${email}`
              : 'Enter the 6-digit code from your authenticator app'
            }
          </CardDescription>
          {isEmailMode && (
            <p className="text-xs text-amber-500 mt-2">⏱️ Code expires in 5 minutes</p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {mfaMethod === 'totp' && factorsLoading && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading authenticator...
            </div>
          )}
          {isTotpUnavailable && (
            <div className="text-sm text-amber-500 bg-amber-500/10 border border-amber-500/30 px-3 py-2 rounded">
              No authenticator app factor found for this account. Try switching to email or backup code.
            </div>
          )}
          <div className="flex justify-center">
            {/* Hidden text input to help password managers (e.g., Proton Pass) detect OTP */}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              className="absolute h-0 w-0 opacity-0 pointer-events-none"
              aria-hidden="true"
              tabIndex={-1}
            />
            <InputOTP
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
              disabled={isLoading}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="bg-muted/50 border-border" />
                <InputOTPSlot index={1} className="bg-muted/50 border-border" />
                <InputOTPSlot index={2} className="bg-muted/50 border-border" />
                <InputOTPSlot index={3} className="bg-muted/50 border-border" />
                <InputOTPSlot index={4} className="bg-muted/50 border-border" />
                <InputOTPSlot index={5} className="bg-muted/50 border-border" />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* ALTCHA widget for email verification actions */}
          {isEmailMode && (
            <AltchaWidget onToken={setAltchaToken} className="flex justify-center" />
          )}

          <Button 
            onClick={verifyCode} 
            className="w-full" 
            disabled={isLoading || code.length !== 6 || (mfaMethod === 'totp' && (!totpFactorId || factorsLoading)) || (isEmailMode && !altchaToken)}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          {isEmailMode && (
            <Button
              variant="ghost"
              onClick={sendEmailCode}
              disabled={isSending || cooldown > 0}
              className="w-full"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : cooldown > 0 ? (
                `Resend code in ${cooldown}s`
              ) : (
                'Resend Code'
              )}
            </Button>
          )}

          {/* Method switching for users with multiple methods */}
          {availableMethods.length > 1 && recoveryMode === 'none' && onSwitchMethod && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground text-center">Use a different verification method:</p>
              <div className="flex gap-2">
                {mfaMethod !== 'totp' && availableMethods.includes('totp') && (
                  <Button
                    variant="ghost"
                    onClick={() => onSwitchMethod('totp')}
                    className="flex-1 text-muted-foreground text-sm"
                    size="sm"
                  >
                    <Smartphone className="h-4 w-4 mr-1" />
                    Auth App
                  </Button>
                )}
                {mfaMethod !== 'email' && availableMethods.includes('email') && (
                  <Button
                    variant="ghost"
                    onClick={() => onSwitchMethod('email')}
                    className="flex-1 text-muted-foreground text-sm"
                    size="sm"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={handleUseBackupCode}
                  className="flex-1 text-muted-foreground text-sm"
                  size="sm"
                >
                  <Key className="h-4 w-4 mr-1" />
                  Backup
                </Button>
              </div>
            </div>
          )}

          {/* Recovery options for TOTP users with single method */}
          {mfaMethod === 'totp' && recoveryMode === 'none' && availableMethods.length <= 1 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground text-center">Can't access your authenticator?</p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={handleUseEmailRecovery}
                  className="flex-1 text-muted-foreground text-sm"
                  size="sm"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleUseBackupCode}
                  className="flex-1 text-muted-foreground text-sm"
                  size="sm"
                >
                  <Key className="h-4 w-4 mr-1" />
                  Backup Code
                </Button>
              </div>
            </div>
          )}

          {/* Recovery option for email users with single method */}
          {mfaMethod === 'email' && recoveryMode === 'none' && availableMethods.length <= 1 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground text-center">Can't access your email?</p>
              <Button
                variant="ghost"
                onClick={handleUseBackupCode}
                className="w-full text-muted-foreground text-sm"
                size="sm"
              >
                <Key className="h-4 w-4 mr-1" />
                Use Backup Code
              </Button>
            </div>
          )}

          {/* Back to TOTP option when using email recovery */}
          {recoveryMode === 'email' && (
            <Button
              variant="ghost"
              onClick={handleBackToTotp}
              className="w-full text-muted-foreground"
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Back to authenticator app
            </Button>
          )}

          <Button variant="outline" onClick={onCancel} className="w-full">
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
